import { useState } from 'react';
import Navigation from '../../components/feature/Navigation';
import Footer from '../home/components/Footer';
import SEOHead from '../../components/SEOHead';

export default function About() {
  const [activeFaq, setActiveFaq] = useState<number | null>(null);
  const siteUrl = import.meta.env.VITE_SITE_URL || '';

  const handleBookClick = () => {
    window.REACT_APP_NAVIGATE('/book-treatment');
  };

  const priceComparisons = [
    { treatment: 'Dental Implants (per tooth)', turkey: '$500–800', usa: '$3,000–6,000', uk: '$2,500–4,500', germany: '$2,000–4,000', savings: '70–85%' },
    { treatment: 'Porcelain Veneers (per tooth)', turkey: '$200–350', usa: '$1,000–2,500', uk: '$800–1,500', germany: '$700–1,200', savings: '65–80%' },
    { treatment: 'All-on-4 Implants (full arch)', turkey: '$4,500–7,000', usa: '$20,000–40,000', uk: '$15,000–30,000', germany: '$12,000–25,000', savings: '75–85%' },
    { treatment: 'Smile Makeover (full)', turkey: '$3,000–6,000', usa: '$15,000–35,000', uk: '$12,000–25,000', germany: '$10,000–20,000', savings: '70–80%' },
    { treatment: 'Teeth Whitening', turkey: '$150–300', usa: '$500–1,500', uk: '$400–1,000', germany: '$350–800', savings: '60–75%' },
    { treatment: 'Root Canal Treatment', turkey: '$100–200', usa: '$800–1,500', uk: '$600–1,200', germany: '$500–1,000', savings: '75–85%' },
  ];

  const faqs = [
    { question: 'Is dental treatment in Turkey safe?', answer: 'Absolutely. All our partner clinics are JCI-accredited and meet international healthcare standards. Our unique supervision system adds an extra layer of safety, with independent doctors or your own dentist monitoring every step of your treatment.' },
    { question: 'How does the third-party doctor supervision work?', answer: 'An independent doctor with no clinic affiliation reviews all your X-rays, panoramic scans, and treatment plans before any procedure begins. They monitor progress at each stage and verify final results. This ensures complete objectivity and quality control.' },
    { question: 'Can my dentist back home really supervise my treatment abroad?', answer: 'Yes! Your dentist receives all treatment records, X-rays, and progress updates throughout your journey. They can provide input, stay informed of every step, and coordinate follow-up care when you return home. They earn 10% of your treatment cost for their involvement.' },
    { question: 'What if something goes wrong after I return home?', answer: 'All treatments come with comprehensive warranties. Your supervising doctor coordinates any follow-up care needed. We also provide 24/7 support and work with local dentists in your country if adjustments are needed.' },
    { question: 'How much can I really save compared to my home country?', answer: 'Patients typically save 60–85% compared to USA, UK, and European prices. For example, dental implants cost $500–800 in Turkey vs $3,000–6,000 in the USA. Even with travel costs, the savings are substantial.' },
    { question: 'Do I need to stay in Turkey for the entire treatment?', answer: 'Treatment duration varies by procedure. Simple treatments like veneers take 5–7 days, while implants may require 2 visits over 3–6 months. We provide detailed timelines and help arrange accommodation and travel.' },
    { question: 'What languages do you support?', answer: 'We provide services in English, Turkish, Arabic, German, and French. All our clinics have multilingual staff, and your treatment coordinator will speak your language fluently.' },
    { question: 'How do I pay for treatments?', answer: 'We accept international credit cards, bank transfers, and payment plans. Payment is typically split: 30% deposit to secure your booking, 70% before treatment begins. All prices are transparent with no hidden fees.' },
    { question: 'What\'s included in the treatment package?', answer: 'Treatment packages include: all dental procedures, supervision fees (if applicable), pre-treatment consultations, X-rays and scans, post-treatment check-ups, and lifetime warranty. Airport transfers and accommodation can be added.' },
    { question: 'How do I choose between the three supervision options?', answer: 'Choose third-party supervision for maximum objectivity and peace of mind. Choose your own doctor if you want continuity of care and have a trusted dentist back home. Choose our expert if you prefer included supervision with no extra cost.' },
  ];

  const schema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        '@id': `${siteUrl}/about`,
        url: `${siteUrl}/about`,
        name: 'About Plexus Alliance — Doctor-Supervised Dental Tourism in Turkey',
        description: 'Learn how Plexus Alliance keeps your dental journey safe with independent third-party doctor supervision, your own dentist, or our in-house expert.',
        breadcrumb: {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
            { '@type': 'ListItem', position: 2, name: 'About', item: `${siteUrl}/about` },
          ],
        },
      },
      {
        '@type': 'FAQPage',
        mainEntity: faqs.map((faq) => ({
          '@type': 'Question',
          name: faq.question,
          acceptedAnswer: { '@type': 'Answer', text: faq.answer },
        })),
      },
    ],
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <SEOHead
        title="About Plexus Alliance | Doctor-Supervised Dental Care in Turkey — How It Works"
        description="Discover our unique 3-option doctor supervision model for dental tourism in Turkey. Independent doctors, your own dentist, or our in-house expert. JCI-accredited clinics, 60–85% savings vs USA & UK."
        keywords="dental tourism supervision Turkey, third-party doctor supervision, dental care Turkey safety, JCI dental clinic Istanbul, dental treatment cost comparison"
        canonical={`${siteUrl}/about`}
        schema={schema}
      />
      <Navigation />

      {/* ── HERO ── */}
      <section className="relative min-h-[80vh] flex flex-col justify-center overflow-hidden pt-28 pb-20 md:pt-36 md:pb-28">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/de72081c67d94b8695f001192212e43a.jpg"
            alt="Luxury dental clinic"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-black/50" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-transparent to-black/40" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 w-full">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-12 h-px bg-amber-400/60" />
            <span className="text-amber-400/80 text-xs font-medium tracking-[0.3em] uppercase" style={{ fontFamily: "'Inter', sans-serif" }}>
              Trust &amp; Safety First
            </span>
          </div>
          <h1
            className="text-5xl md:text-7xl lg:text-8xl font-normal text-white leading-[1.05] mb-6 max-w-4xl"
            style={{ fontFamily: "'Playfair Display', serif", letterSpacing: '-0.01em' }}
          >
            How we keep your
            <br />
            <em className="text-amber-300 not-italic font-medium">dental journey safe.</em>
          </h1>
          <p className="text-base md:text-lg text-white/50 max-w-xl font-light leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
            We've revolutionized dental tourism with a unique supervision system that puts your safety first. Choose who monitors your treatment for complete peace of mind.
          </p>
        </div>
      </section>

      {/* ── THREE SUPERVISION OPTIONS ── */}
      <section className="py-20 md:py-28 bg-[#0f0f0f]">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="mb-14 md:mb-20">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-px bg-amber-400/60" />
              <span className="text-amber-400/80 text-xs font-medium tracking-[0.3em] uppercase" style={{ fontFamily: "'Inter', sans-serif" }}>
                Supervision Options
              </span>
            </div>
            <h2
              className="text-4xl md:text-6xl font-normal text-white leading-tight"
              style={{ fontFamily: "'Playfair Display', serif", letterSpacing: '-0.01em' }}
            >
              Three ways to stay
              <br />
              <em className="text-amber-300 not-italic">in control.</em>
            </h2>
          </div>

          <div className="space-y-px bg-white/5 border border-white/5">
            {/* Option 1 */}
            <div className="bg-[#0f0f0f] p-8 md:p-14">
              <div className="grid md:grid-cols-2 gap-10 md:gap-16 items-center">
                <div>
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-8 h-8 flex items-center justify-center border border-amber-400/30">
                      <i className="ri-shield-user-line text-amber-400 text-base" />
                    </div>
                    <span className="text-amber-400/80 text-xs font-medium tracking-[0.25em] uppercase" style={{ fontFamily: "'Inter', sans-serif" }}>Most Trusted</span>
                  </div>
                  <h3 className="text-2xl md:text-3xl font-normal text-white mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                    Independent Third-Party Doctor
                  </h3>
                  <p className="text-white/40 text-sm font-light leading-relaxed mb-8" style={{ fontFamily: "'Inter', sans-serif" }}>
                    An independent doctor with no clinic affiliation supervises your entire treatment journey from start to finish.
                  </p>
                  <div className="space-y-4">
                    {['Pre-Treatment Review of all X-rays and scans', 'Treatment Plan Approval before any procedure', 'Progress Monitoring at each stage', 'Final Verification before you leave Turkey', 'No Conflict of Interest — paid by you, not the clinic'].map((item, i) => (
                      <div key={i} className="flex items-start gap-3">
                        <div className="w-4 h-4 flex items-center justify-center mt-0.5 flex-shrink-0">
                          <i className="ri-checkbox-circle-line text-amber-400 text-sm" />
                        </div>
                        <span className="text-white/50 text-sm font-light" style={{ fontFamily: "'Inter', sans-serif" }}>{item}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-8 border border-white/10 p-5">
                    <div className="text-white/25 text-xs tracking-widest uppercase mb-1" style={{ fontFamily: "'Inter', sans-serif" }}>Supervision Cost</div>
                    <div className="text-2xl font-normal text-amber-300" style={{ fontFamily: "'Playfair Display', serif" }}>Fixed Fee</div>
                    <div className="text-white/30 text-xs mt-1" style={{ fontFamily: "'Inter', sans-serif" }}>Typically $300–500 depending on treatment complexity</div>
                  </div>
                </div>
                <div className="relative h-72 md:h-96 overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=professional%20independent%20medical%20doctor%20reviewing%20dental%20xrays%20on%20large%20monitor%20dark%20elegant%20office%20warm%20amber%20lighting%20sophisticated%20medical%20environment%20moody%20editorial%20photography%20premium%20healthcare&width=800&height=600&seq=about-option1-dark-01&orientation=landscape"
                    alt="Independent Third-Party Doctor"
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0f0f0f]/60 to-transparent" />
                </div>
              </div>
            </div>

            {/* Option 2 */}
            <div className="bg-[#0a0a0a] p-8 md:p-14">
              <div className="grid md:grid-cols-2 gap-10 md:gap-16 items-center">
                <div className="order-2 md:order-1 relative h-72 md:h-96 overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=trusted%20dentist%20consulting%20with%20patient%20showing%20dental%20records%20on%20tablet%20dark%20elegant%20clinic%20warm%20amber%20lighting%20professional%20relationship%20trust%20care%20moody%20editorial%20photography%20premium%20medical%20environment&width=800&height=600&seq=about-option2-dark-01&orientation=landscape"
                    alt="Your Own Doctor Back Home"
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a]/60 to-transparent" />
                </div>
                <div className="order-1 md:order-2">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-8 h-8 flex items-center justify-center border border-amber-400/30">
                      <i className="ri-user-heart-line text-amber-400 text-base" />
                    </div>
                    <span className="text-amber-400/80 text-xs font-medium tracking-[0.25em] uppercase" style={{ fontFamily: "'Inter', sans-serif" }}>Most Comfortable</span>
                  </div>
                  <h3 className="text-2xl md:text-3xl font-normal text-white mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                    Bring Your Own Doctor
                  </h3>
                  <p className="text-white/40 text-sm font-light leading-relaxed mb-8" style={{ fontFamily: "'Inter', sans-serif" }}>
                    Keep your trusted dentist back home involved throughout your treatment abroad. They stay informed and coordinate your continued care.
                  </p>
                  <div className="space-y-4">
                    {['Full Treatment Records sent to your dentist', 'Real-Time Updates at every treatment step', 'Continuity of Care for seamless follow-ups', 'Professional Compensation — 10% of treatment cost', 'Local Follow-Up handled by your own dentist'].map((item, i) => (
                      <div key={i} className="flex items-start gap-3">
                        <div className="w-4 h-4 flex items-center justify-center mt-0.5 flex-shrink-0">
                          <i className="ri-checkbox-circle-line text-amber-400 text-sm" />
                        </div>
                        <span className="text-white/50 text-sm font-light" style={{ fontFamily: "'Inter', sans-serif" }}>{item}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-8 border border-white/10 p-5">
                    <div className="text-white/25 text-xs tracking-widest uppercase mb-1" style={{ fontFamily: "'Inter', sans-serif" }}>Supervision Cost</div>
                    <div className="text-2xl font-normal text-amber-300" style={{ fontFamily: "'Playfair Display', serif" }}>10% of Treatment Cost</div>
                    <div className="text-white/30 text-xs mt-1" style={{ fontFamily: "'Inter', sans-serif" }}>Paid directly to your dentist for their oversight</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Option 3 */}
            <div className="bg-[#0f0f0f] p-8 md:p-14">
              <div className="grid md:grid-cols-2 gap-10 md:gap-16 items-center">
                <div>
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-8 h-8 flex items-center justify-center border border-amber-400/30">
                      <i className="ri-team-line text-amber-400 text-base" />
                    </div>
                    <span className="text-amber-400/80 text-xs font-medium tracking-[0.25em] uppercase" style={{ fontFamily: "'Inter', sans-serif" }}>Included</span>
                  </div>
                  <h3 className="text-2xl md:text-3xl font-normal text-white mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                    Our In-House Expert
                  </h3>
                  <p className="text-white/40 text-sm font-light leading-relaxed mb-8" style={{ fontFamily: "'Inter', sans-serif" }}>
                    One of our experienced dental specialists with 15+ years of expertise oversees your treatment from start to finish.
                  </p>
                  <div className="space-y-4">
                    {['15+ Years of international dental expertise', 'On-Site Supervision at the clinic in person', 'Immediate Response to any concerns', 'Quality Assurance at every step', 'No Extra Cost — included in your package'].map((item, i) => (
                      <div key={i} className="flex items-start gap-3">
                        <div className="w-4 h-4 flex items-center justify-center mt-0.5 flex-shrink-0">
                          <i className="ri-checkbox-circle-line text-amber-400 text-sm" />
                        </div>
                        <span className="text-white/50 text-sm font-light" style={{ fontFamily: "'Inter', sans-serif" }}>{item}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-8 border border-white/10 p-5">
                    <div className="text-white/25 text-xs tracking-widest uppercase mb-1" style={{ fontFamily: "'Inter', sans-serif" }}>Supervision Cost</div>
                    <div className="text-2xl font-normal text-amber-300" style={{ fontFamily: "'Playfair Display', serif" }}>Included in Package</div>
                    <div className="text-white/30 text-xs mt-1" style={{ fontFamily: "'Inter', sans-serif" }}>No additional fees — part of your comprehensive package</div>
                  </div>
                </div>
                <div className="relative h-72 md:h-96 overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=experienced%20dental%20specialist%20expert%20dark%20elegant%20clinic%20examining%20dental%20models%20confident%20professional%20expression%20warm%20amber%20lighting%20moody%20editorial%20photography%20premium%20healthcare%20environment%20Istanbul%20Turkey&width=800&height=600&seq=about-option3-dark-01&orientation=landscape"
                    alt="Our In-House Expert"
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0f0f0f]/60 to-transparent" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── PRICE COMPARISON ── */}
      <section className="py-20 md:py-28 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="mb-14 md:mb-20">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-px bg-amber-400/60" />
              <span className="text-amber-400/80 text-xs font-medium tracking-[0.3em] uppercase" style={{ fontFamily: "'Inter', sans-serif" }}>
                Transparent Pricing
              </span>
            </div>
            <h2
              className="text-4xl md:text-6xl font-normal text-white leading-tight"
              style={{ fontFamily: "'Playfair Display', serif", letterSpacing: '-0.01em' }}
            >
              Cost Comparison:
              <br />
              <em className="text-amber-300 not-italic">Turkey vs. the World.</em>
            </h2>
          </div>

          <div className="border border-white/10 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[640px]">
                <thead>
                  <tr className="border-b border-white/10 bg-[#111111]">
                    <th className="px-5 md:px-8 py-4 text-left text-xs font-medium tracking-widest uppercase text-white/30" style={{ fontFamily: "'Inter', sans-serif" }}>Treatment</th>
                    <th className="px-5 md:px-8 py-4 text-center text-xs font-medium tracking-widest uppercase text-amber-400/80" style={{ fontFamily: "'Inter', sans-serif" }}>Turkey</th>
                    <th className="px-5 md:px-8 py-4 text-center text-xs font-medium tracking-widest uppercase text-white/30" style={{ fontFamily: "'Inter', sans-serif" }}>USA</th>
                    <th className="px-5 md:px-8 py-4 text-center text-xs font-medium tracking-widest uppercase text-white/30" style={{ fontFamily: "'Inter', sans-serif" }}>UK</th>
                    <th className="px-5 md:px-8 py-4 text-center text-xs font-medium tracking-widest uppercase text-white/30" style={{ fontFamily: "'Inter', sans-serif" }}>Germany</th>
                    <th className="px-5 md:px-8 py-4 text-center text-xs font-medium tracking-widest uppercase text-white/30" style={{ fontFamily: "'Inter', sans-serif" }}>Savings</th>
                  </tr>
                </thead>
                <tbody>
                  {priceComparisons.map((item, index) => (
                    <tr key={index} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors">
                      <td className="px-5 md:px-8 py-4 text-white/60 text-sm font-light" style={{ fontFamily: "'Inter', sans-serif" }}>{item.treatment}</td>
                      <td className="px-5 md:px-8 py-4 text-center">
                        <span className="text-amber-400 text-sm font-medium" style={{ fontFamily: "'Inter', sans-serif" }}>{item.turkey}</span>
                      </td>
                      <td className="px-5 md:px-8 py-4 text-center text-white/25 text-sm font-light" style={{ fontFamily: "'Inter', sans-serif" }}>{item.usa}</td>
                      <td className="px-5 md:px-8 py-4 text-center text-white/25 text-sm font-light" style={{ fontFamily: "'Inter', sans-serif" }}>{item.uk}</td>
                      <td className="px-5 md:px-8 py-4 text-center text-white/25 text-sm font-light" style={{ fontFamily: "'Inter', sans-serif" }}>{item.germany}</td>
                      <td className="px-5 md:px-8 py-4 text-center">
                        <span className="text-xs font-medium text-amber-400 border border-amber-400/20 bg-amber-400/5 px-3 py-1" style={{ fontFamily: "'Inter', sans-serif" }}>
                          -{item.savings}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-px bg-white/5 border border-white/5">
            {[
              { icon: 'ri-flight-takeoff-line', title: 'Even With Travel', desc: 'Including flights and accommodation, you still save 50–70% compared to home country prices.' },
              { icon: 'ri-shield-check-line', title: 'Same Quality', desc: 'JCI-accredited clinics with the same equipment and materials used in USA and Europe.' },
              { icon: 'ri-time-line', title: 'Quick Treatment', desc: 'Most treatments completed in 5–7 days, combining dental care with a Turkish vacation.' },
            ].map((item, i) => (
              <div key={i} className="bg-[#0a0a0a] p-8 md:p-10">
                <div className="w-10 h-10 flex items-center justify-center mb-5">
                  <i className={`${item.icon} text-2xl text-amber-400/60`} />
                </div>
                <h4 className="text-base font-medium text-white/70 mb-2" style={{ fontFamily: "'Inter', sans-serif" }}>{item.title}</h4>
                <p className="text-white/30 text-sm font-light leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── TRUST STATS ── */}
      <section className="py-20 md:py-28 bg-[#0f0f0f]">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/5 border border-white/5">
            {[
              { value: '50K+', label: 'Patients Yearly' },
              { value: '98%', label: 'Satisfaction Rate' },
              { value: '15+', label: 'Years Experience' },
              { value: '100%', label: 'JCI Accredited' },
            ].map((s, i) => (
              <div key={i} className="bg-[#0f0f0f] p-10 md:p-14 text-center">
                <div className="text-4xl md:text-5xl font-normal text-amber-300 mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>{s.value}</div>
                <div className="text-xs text-white/25 tracking-widest uppercase" style={{ fontFamily: "'Inter', sans-serif" }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="py-20 md:py-28 bg-[#0a0a0a]">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <div className="mb-14 md:mb-20">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-px bg-amber-400/60" />
              <span className="text-amber-400/80 text-xs font-medium tracking-[0.3em] uppercase" style={{ fontFamily: "'Inter', sans-serif" }}>
                Common Questions
              </span>
            </div>
            <h2
              className="text-4xl md:text-5xl font-normal text-white leading-tight"
              style={{ fontFamily: "'Playfair Display', serif", letterSpacing: '-0.01em' }}
            >
              Frequently Asked
              <br />
              <em className="text-amber-300 not-italic">Questions.</em>
            </h2>
          </div>

          <div className="space-y-px border border-white/5">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-[#0f0f0f] border-b border-white/5 last:border-b-0">
                <button
                  onClick={() => setActiveFaq(activeFaq === index ? null : index)}
                  className="w-full px-6 md:px-8 py-5 md:py-6 flex items-center justify-between text-left hover:bg-white/[0.02] transition-colors cursor-pointer"
                >
                  <h3 className="text-sm md:text-base font-medium text-white/70 pr-4" style={{ fontFamily: "'Inter', sans-serif" }}>{faq.question}</h3>
                  <i className={`ri-${activeFaq === index ? 'subtract' : 'add'}-line text-amber-400/60 flex-shrink-0 text-lg`} />
                </button>
                {activeFaq === index && (
                  <div className="px-6 md:px-8 pb-5 md:pb-6">
                    <p className="text-white/35 text-sm font-light leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="relative overflow-hidden py-24 md:py-32">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Istanbul%20Turkey%20skyline%20at%20dusk%20golden%20hour%20luxury%20city%20view%20Bosphorus%20bridge%20warm%20amber%20tones%20moody%20atmospheric%20photography%20dark%20elegant%20premium%20editorial%20aesthetic&width=1440&height=700&seq=about-cta-dark-01&orientation=landscape"
            alt="Istanbul"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/75 via-black/65 to-black/80" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-10 text-center">
          <div className="flex items-center justify-center gap-4 mb-8">
            <div className="w-12 h-px bg-amber-400/60" />
            <span className="text-amber-400/80 text-xs font-medium tracking-[0.3em] uppercase" style={{ fontFamily: "'Inter', sans-serif" }}>
              Begin Your Journey
            </span>
            <div className="w-12 h-px bg-amber-400/60" />
          </div>
          <h2
            className="text-4xl md:text-6xl font-normal text-white leading-[1.05] mb-6"
            style={{ fontFamily: "'Playfair Display', serif", letterSpacing: '-0.01em' }}
          >
            Ready to start your
            <br />
            <em className="text-amber-300 not-italic">dental journey?</em>
          </h2>
          <p className="text-white/40 text-base font-light mb-12 max-w-xl mx-auto leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
            Book a free consultation to discuss your treatment options and supervision preferences. No commitment required.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={handleBookClick}
              className="bg-amber-400 hover:bg-amber-300 text-black px-10 py-4 font-semibold text-sm tracking-wide transition-all hover:scale-105 cursor-pointer whitespace-nowrap"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              Book Free Consultation
              <i className="ri-arrow-right-line ml-2" />
            </button>
            <a
              href="https://wa.me/message/OAKDCTBVIML6F1"
              target="_blank"
              rel="noopener noreferrer"
              className="border border-white/20 text-white/70 hover:text-white hover:border-white/40 px-10 py-4 font-medium text-sm tracking-wide transition-all cursor-pointer whitespace-nowrap"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              <i className="ri-whatsapp-line mr-2" />
              Chat on WhatsApp
            </a>
          </div>
        </div>
      </section>

      <Footer onBookClick={handleBookClick} />
    </div>
  );
}
